import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

export interface IAddTemplateToIndustryParams {
	industry: string;
	templateId: string;
	status?: Api.TemplateStatus;
}

export interface IAddTemplateToIndustryOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
}

/**
 * Admin: Add a survey template to an industry with optional status.
 * Associates the template with the specified industry.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const useAddTemplateToIndustryMutation = (options: IAddTemplateToIndustryOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: IAddTemplateToIndustryParams) => {
			const { industry, templateId, status } = params;

			// Build query string for templateId and optional status
			const queryParams = new URLSearchParams({ templateId });
			if (status) {
				queryParams.append('status', status);
			}

			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/surveytemplate/Industry/${industry}?${queryParams.toString()}`,
				}),
				'POST'
			);
		},
		onSuccess: () => {
			// Invalidate relevant queries
			reactQueryClient.invalidateQueries({ queryKey: ['systemSurveyTemplate'] });
			reactQueryClient.invalidateQueries({ queryKey: ['surveyTemplatesByIndustry'] });
			onSuccess?.();
		},
		onError,
	});
};
